import { EMPTY, Observable, throwError } from 'rxjs';
import { AjaxConfig, AjaxError, AjaxResponse } from 'rxjs/ajax';
import { catchError } from 'rxjs/operators';

import { HttpCodes } from 'app/constants/http-codes';
import { ElasticSearchError } from 'app/model/errors/elastic-search-error';
import { FormError } from 'app/model/form-error';
import { FormErrorResponse } from 'app/model/form-error-response';
import { formErrorService } from 'app/services/form-error.service';
import { messagingService } from 'app/services/messaging.service';
import { AppStore } from 'app/store';

import { HttpHandler } from '../http-handler';
import { Interceptor } from '../interceptor';

export class ErrorInterceptor implements Interceptor {
    public intercept<T>(store: AppStore, req: AjaxConfig, next: HttpHandler<T>): Observable<AjaxResponse<T>> {
        return next.handle(req).pipe(
            catchError((error: AjaxError) => {
                try {
                    const body = (
                        'object' === typeof error.response ? error.response : JSON.parse(error.response as string)
                    ) as Record<string, unknown>;

                    if (HttpCodes.SERVER_ERROR <= error.status) {
                        if (ErrorInterceptor.isElasticError(body)) {
                            messagingService.showError(
                                `Elastic Search Error: ${body.message.error.root_cause[0].reason}`,
                            );

                            return EMPTY;
                        }
                        let message = (body.title as string) || 'Error';
                        message += ': ';
                        message += (body.detail as string) || 'Unexpected Error';
                        messagingService.showError(message);

                        return EMPTY;
                    }

                    if (HttpCodes.BAD_REQUEST === error.status && ErrorInterceptor.isFormError(body)) {
                        if (body.errors.errors && 0 < body.errors.errors.length) {
                            // general form error unrelated to any field
                            messagingService.showError(body.errors.errors.join('; '));
                        }

                        formErrorService.handleFormErrors(body.errors.children);

                        return EMPTY;
                    }

                    if (HttpCodes.BAD_REQUEST === error.status && body.message) {
                        // bad request with message, but not form error
                        messagingService.showError(body.message as string);
                    }

                    if (HttpCodes.NOT_FOUND === error.status && body.message) {
                        // bad request with message, but not form error
                        messagingService.showError(body.message as string);
                    }
                } catch (ex) {
                    // likely not a server json error
                }

                if (HttpCodes.GATEWAY_TIMEOUT === error.status) {
                    // bad request with message, but not form error
                    messagingService.showError('Backend Request TIMED OUT!' as string);
                }

                return throwError(() => error);
            }),
        );
    }

    private static isFormError(body: Partial<FormErrorResponse>): body is FormErrorResponse {
        return (
            body.code === HttpCodes.BAD_REQUEST &&
            undefined !== body.message &&
            'object' === typeof body.errors &&
            'object' === typeof (body.errors as Partial<FormError>).children
        );
    }

    private static isElasticError(body: Partial<ElasticSearchError>): body is ElasticSearchError {
        if (body.code !== HttpCodes.SERVER_ERROR || undefined === body.message) {
            return false;
        }

        try {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const messageData = JSON.parse(body.message as unknown as string) as { error?: { root_cause?: unknown } };

            if (messageData.error !== undefined && messageData.error.root_cause !== undefined) {
                body.message = messageData as typeof body.message;

                return true;
            }
        } catch (err) {
            // not json, so not ES error
        }

        return false;
    }
}
